/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * The traffic-light scale of a patient result card, computed in one place so
 * the on-screen card (HeartReportCards) and the patient report document
 * (patient-report-document) cannot drift: which track variant a feature
 * uses, that variant's gradient color stops, the zone labels with their
 * widths, and the marker position.
 *
 * The gradients are painted by heart-report.css (.scale-track--uni /
 * .scale-track--bi); SCALE_GRADIENT_STOPS mirrors those declarations as data
 * for renderers that cannot use the stylesheet (the server-side PDF), and a
 * unit test pins the two against each other.
 */

import {
  BIDIRECTIONAL_ZONES,
  ReportFeature,
  UNIDIRECTIONAL_ZONES,
  calculateMarkerPosition,
  displayMarkerPosition,
  isCardiacCycleTimingFeature,
  isRhythmRegularityFeature,
  timingRangeOf,
} from "./core"

type ScaleTrackVariant = "scale-track--uni" | "scale-track--bi"

type ScaleGradientStop = { color: string; pct: number }

type ScaleLabel = { text: string; basisPct: number }

/** The two ends of a range on the track, percent from the left, each with the title its marker carries. */
type ScaleRange = { minPct: number; maxPct: number; minLabel: string; maxLabel: string }

// The zone colors of the scale tracks (green / amber / red), as the CSS
// gradients paint them.
const GREEN = "#c8e6c9"
const AMBER = "#ffe0b2"
const RED = "#ffcdd2"

/**
 * The color stops of the two scale-track gradients, left to right, exactly
 * as heart-report.css declares them (hard-edged zones: each boundary is a
 * pair of stops at the same percentage). The stop positions are the zone
 * constants; a unit test asserts these arrays match the stylesheet. The
 * cardiac-cycle-timing gradient computed from BIDIRECTIONAL_ZONES in the
 * prototype equals the bidirectional default, so two variants cover every
 * card.
 */
const SCALE_GRADIENT_STOPS: Record<ScaleTrackVariant, ScaleGradientStop[]> = {
  "scale-track--uni": [
    { color: GREEN, pct: 0 },
    { color: GREEN, pct: UNIDIRECTIONAL_ZONES.greenEnd },
    { color: AMBER, pct: UNIDIRECTIONAL_ZONES.greenEnd },
    { color: AMBER, pct: UNIDIRECTIONAL_ZONES.amberEnd },
    { color: RED, pct: UNIDIRECTIONAL_ZONES.amberEnd },
    { color: RED, pct: 100 },
  ],
  "scale-track--bi": [
    { color: RED, pct: 0 },
    { color: RED, pct: BIDIRECTIONAL_ZONES.redLowEnd },
    { color: AMBER, pct: BIDIRECTIONAL_ZONES.redLowEnd },
    { color: AMBER, pct: BIDIRECTIONAL_ZONES.amberLowEnd },
    { color: GREEN, pct: BIDIRECTIONAL_ZONES.amberLowEnd },
    { color: GREEN, pct: BIDIRECTIONAL_ZONES.greenEnd },
    { color: AMBER, pct: BIDIRECTIONAL_ZONES.greenEnd },
    { color: AMBER, pct: BIDIRECTIONAL_ZONES.amberHighEnd },
    { color: RED, pct: BIDIRECTIONAL_ZONES.amberHighEnd },
    { color: RED, pct: 100 },
  ],
}

// getScaleGradientStyle (prototype 5313-5324), as a track modifier class.
const scaleTrackVariant = (feature: ReportFeature): ScaleTrackVariant =>
  isRhythmRegularityFeature(feature) ||
  (feature.scaleType === "unidirectional" && !isCardiacCycleTimingFeature(feature))
    ? "scale-track--uni"
    : "scale-track--bi"

/** The gradient stops of the track the feature's card renders. */
const scaleGradientStops = (feature: ReportFeature): ScaleGradientStop[] =>
  SCALE_GRADIENT_STOPS[scaleTrackVariant(feature)]

// getScaleLabelsHTML (prototype 5326-5342), as data. basisPct is the label's
// flex-basis, i.e. the width of the zone it names.
const scaleLabels = (feature: ReportFeature): ScaleLabel[] => {
  if (isRhythmRegularityFeature(feature) || feature.scaleType === "unidirectional") {
    return [
      { text: "Normal", basisPct: 58 },
      { text: "B'line", basisPct: 22 },
      { text: "Elevated", basisPct: 20 },
    ]
  }
  const z = BIDIRECTIONAL_ZONES
  return [
    { text: "Low", basisPct: z.redLowEnd },
    { text: "B'line", basisPct: z.amberLowEnd - z.redLowEnd },
    { text: "Norm", basisPct: z.greenEnd - z.amberLowEnd },
    { text: "B'line", basisPct: z.amberHighEnd - z.greenEnd },
    { text: "Elevated", basisPct: 100 - z.amberHighEnd },
  ]
}

/** The marker position (0-100) the feature's card renders the dot at. */
const scaleMarkerPct = (feature: ReportFeature, hrBpm?: number | null): number =>
  displayMarkerPosition(feature.markerPosition ?? calculateMarkerPosition(feature, hrBpm))

/**
 * Where a timing card's range sits on its track — one marker at each end,
 * joined by a band — or null when the card shows a single marker: a Heart
 * Rate card's range, a SYS:DIA Ratio card's, or the spread of a SYS:DIA
 * Duration card's included recordings (Heart Report v0.5.8).
 */
const scaleRangePct = (feature: ReportFeature, hrBpm?: number | null): ScaleRange | null => {
  const range = timingRangeOf(feature, hrBpm)
  if (!range) {
    return null
  }
  const ascending = range.minPos <= range.maxPos
  return {
    minPct: Math.min(range.minPos, range.maxPos),
    maxPct: Math.max(range.minPos, range.maxPos),
    minLabel: ascending ? range.minLabel : range.maxLabel,
    maxLabel: ascending ? range.maxLabel : range.minLabel,
  }
}

export { SCALE_GRADIENT_STOPS, scaleGradientStops, scaleLabels, scaleMarkerPct, scaleRangePct, scaleTrackVariant }
export type { ScaleGradientStop, ScaleLabel, ScaleRange, ScaleTrackVariant }
